'use client'

import PropTypes from 'prop-types'
import { useRouteError } from 'react-router-dom'
import MUIStack from '@mui/material/Stack'
import MUIButton from '@mui/material/Button'
import MUITypography from '@mui/material/Typography'
import { OspreyIllustration } from '@repo/ui/OspreyIllustration'
import { openSupportEmail } from '@repo/utils/emailSupport'
import { reactPlugin } from '@repo/app-assets/ApplicationInsights'

export const PageErrorBoundary = () => {
  const error = useRouteError()
  console.error(error)
  reactPlugin.trackException({ error, id: JSON.stringify(error) })

  console.error('Error boundary caught an error:', error)

  return <ErrorContent error={error} />
}

const ErrorContent = ({ error }) => {
  let statusCode = error.status || error.cause?.status
  if (!statusCode && statusCode !== 0) statusCode = ''
  const statusText = error.statusText || error.cause?.statusText || ''
  const message = error.message || error.error?.message || ''

  const reportIssue = () => {
    openSupportEmail(error)
  }

  return (
    <MUIStack
      direction="column"
      justifyContent="center"
      alignItems="center"
      sx={{ width: '100%', height: '100%', maxWidth: '600px', margin: 'auto', textAlign: 'center', padding: '40px' }}
    >
      <OspreyIllustration />
      <MUITypography variant="h2" sx={{ mt: 10 }}>
        {`There's been a problem`}
      </MUITypography>

      <MUITypography variant="body1" sx={{ color: 'text.primary', mb: 6 }}>
        {`We're sorry that something has gone wrong and we can't complete your intended action. Where possible, we have included error details below.`}
      </MUITypography>

      <MUITypography variant="body1" sx={{ color: 'secondary.primary' }}>
        {`If you need help, please click the button below to report this issue via email so we can investigate:`}
      </MUITypography>

      <MUIStack direction="row" gap={3} sx={{ mt: 3 }}>
        <MUIButton variant="outlined" color="primary" sx={{ mt: 6 }} onClick={() => window.history.back()}>
          Go back
        </MUIButton>

        <MUIButton variant="contained" color="primary" sx={{ mt: 6 }} onClick={reportIssue}>
          Report issue
        </MUIButton>
      </MUIStack>

      {message || statusCode || statusText ? (
        <MUIStack
          direction="column"
          justifyContent="center"
          alignItems="flex-start"
          sx={{
            width: '100%',
            maxWidth: '600px',
            textAlign: 'left',
            padding: '30px 40px',
            borderRadius: 2,
            backgroundColor: 'white',
            mt: 12
          }}
        >
          {statusCode || statusText ? (
            <MUITypography variant="h5" sx={{ mt: 2, textAlign: 'left' }}>
              Error:&nbsp;
              {statusCode || ''}
              {statusCode && statusText ? ' | ' : ''}
              {statusText || ''}
            </MUITypography>
          ) : null}

          {message ? (
            <MUITypography variant="body1" sx={{ mb: 0, textAlign: 'left' }}>
              <code>{message}</code>
            </MUITypography>
          ) : null}
        </MUIStack>
      ) : null}
    </MUIStack>
  )
}

ErrorContent.propTypes = {
  error: PropTypes.object,
  userId: PropTypes.string,
  emailAddress: PropTypes.string
}
